import { Box, Collapse, IconButton, ListItem, Skeleton, Typography } from "@mui/material"
import { green, grey, red } from "@mui/material/colors"
import { GroupStatus } from "authoring/lib/enum/group-status.mjs"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { GroupStatusBadge } from "./group-status-badge"
import { useState } from "react"

export function ErrorDialogItem({ item: legacySendResults }) {
    const [showError, setShowError] = useState(false)
    const { scheduleLookup, schedulesLoading } = useBoundContext()
    const { status, scheduleId, error } = legacySendResults
    const { code, title } = scheduleLookup?.[scheduleId.split(":")[0]] ?? {}
    const displayTitle = title ?? scheduleId
    const displayCode = code ?? scheduleId
    const hasError = Boolean(error)

    const applied = status === GroupStatus.APPLIED
    return (
        <ListItem
            data-cy="error-details-item"
            sx={{
                mb: 1,
                borderBottom: "2px solid",
                borderColor: schedulesLoading ? "white" : grey[200],
                minWidth: "400px",
            }}
        >
            <If truthy={schedulesLoading}>
                <Skeleton variant="rounded" width="100%" height={36} />
                <Else>
                    <Box width={"100%"}>
                        <Box
                            display={"flex"}
                            width={"100%"}
                            alignItems={"center"}
                            onClick={handleToggle}
                            sx={{ cursor: hasError && "pointer" }}
                        >
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                mr={2}
                                gap={1}
                                justifyContent={"start"}
                                sx={{ minWidth: 64 }}
                            >
                                <Iconify
                                    icon={
                                        applied
                                            ? "material-symbols:check-circle-rounded"
                                            : "material-symbols:error-outline-rounded"
                                    }
                                    sx={{
                                        color: applied ? green[500] : red[600],
                                        width: 24,
                                        height: 24,
                                    }}
                                />
                                <Iconify icon={"quill:paper"} sx={{ width: 24, height: 24 }} />
                            </Box>
                            <Typography data-cy="error-details-schedule-name" flexGrow={1} minWidth={0} noWrap>
                                {displayTitle}
                            </Typography>
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                justifyContent={"flex-end"}
                                gap={2}
                                minWidth={"176px"}
                                flexShrink={0}
                            >
                                <Typography
                                    data-cy="error-details-schedule-code"
                                    noWrap
                                    textAlign={"left"}
                                    sx={{ minWidth: "96px" }}
                                >
                                    #{displayCode}
                                </Typography>
                                <Box minWidth={"42px"} display={"flex"} justifyContent={"flex-end"}>
                                    <GroupStatusBadge status={status} />
                                </Box>
                            </Box>
                            <IconButton
                                disabled={!hasError}
                                size="small"
                                sx={{ ml: 1 }}
                                aria-label="toggle error details"
                                data-cy="error-details-toggle"
                            >
                                <Iconify icon={showError ? "mingcute:up-fill" : "mingcute:down-fill"} />
                            </IconButton>
                        </Box>
                        <Collapse in={showError} unmountOnExit>
                            <Box
                                mt={1}
                                px={1.5}
                                py={1}
                                border={"1px solid"}
                                borderColor={grey[200]}
                                borderRadius={1}
                                bgcolor={grey[50]}
                                data-cy="error-details-message"
                                sx={{ whiteSpace: "pre-wrap" }}
                            >
                                <Typography variant="body2" color={grey[700]}>
                                    {error}
                                </Typography>
                            </Box>
                        </Collapse>
                    </Box>
                </Else>
            </If>
        </ListItem>
    )

    function handleToggle() {
        if (!hasError) return
        setShowError((prev) => !prev)
    }
}
